import type { GenderDetection } from '../types/genetic';
import type {
  NutritionReport,
  NutritionGeneGroup,
  NutritionVariant,
  NutritionRule,
  Reference,
  TabConfig,
  TabReport,
} from '../types/nutrition';
import { calculateZygosity } from '../analysis/calculate-zygosity';
import { normalizeGenotype } from '../analysis/normalize-genotype';
import { resolveProxies } from '../analysis/resolve-proxies';

type SnpMap = { [rsid: string]: { genotype: string; chromosome: string; position: number } };

export const generateNutritionReport = ({
  snps,
  gender,
  fileInfo,
  tabConfigs,
}: {
  snps: SnpMap;
  gender: GenderDetection;
  fileInfo: { fileName: string; fileSize: number; provider: string };
  tabConfigs: TabConfig[];
}): NutritionReport => {
  const tabs: TabReport[] = [];
  let totalSNPsAnalyzed = 0;
  let totalSNPsFound = 0;

  for (const tabConfig of tabConfigs) {
    const tabReport = generateTabReport({
      tabId: tabConfig.id,
      tabName: tabConfig.name,
      rules: tabConfig.rules,
      snps,
      gender,
    });
    tabs.push(tabReport);
    for (const geneGroup of tabReport.geneGroups) {
      totalSNPsAnalyzed += geneGroup.coverage.total;
      totalSNPsFound += geneGroup.coverage.found;
    }
  }

  const overallCoverage =
    totalSNPsAnalyzed > 0 ? Math.round((totalSNPsFound / totalSNPsAnalyzed) * 100) : 0;

  return {
    analysisDate: new Date(),
    fileInfo,
    gender: { detected: gender.gender, confidence: gender.confidence },
    tabs,
    summary: { totalSNPsAnalyzed, totalSNPsFound, overallCoverage },
  };
};

const generateTabReport = ({
  tabId,
  tabName,
  rules,
  snps,
  gender,
}: {
  tabId: string;
  tabName: string;
  rules: { [gene: string]: { [rsid: string]: NutritionRule } };
  snps: SnpMap;
  gender: GenderDetection;
}): TabReport => {
  const geneGroups: NutritionGeneGroup[] = [];
  let tabSNPsFound = 0;
  let tabSNPsTotal = 0;

  for (const [geneName, geneRules] of Object.entries(rules)) {
    const variants: NutritionVariant[] = [];
    let geneSnpsFound = 0;
    const geneSnpsTotal = Object.keys(geneRules).length;

    for (const [primaryRsid, rule] of Object.entries(geneRules)) {
      tabSNPsTotal++;

      const resolution = resolveProxies({
        snps,
        primaryRsid,
        proxies: rule.proxies || [],
      });

      if (resolution) {
        tabSNPsFound++;
        geneSnpsFound++;

        const normalizedGenotype = normalizeGenotype({
          genotype: resolution.genotype,
          isXLinked: rule.xLinked || false,
          isMale: gender.gender === 'male',
        });

        const zygosity = calculateZygosity({
          genotype: normalizedGenotype,
          riskAlleles: rule.riskAlleles,
          isXLinked: rule.xLinked || false,
          isMale: gender.gender === 'male',
        });

        const effect = rule.effects?.[normalizedGenotype];
        const references = ruleReferences(rule);
        const foundSnpData = snps[resolution.foundRsid];

        variants.push({
          gene: geneName,
          variantName: rule.label,
          rsid: primaryRsid,
          genotype: normalizedGenotype,
          zygosity,
          riskAlleles: rule.riskAlleles,
          testable: rule.testable !== false,
          evidenceLevel: rule.evidence,
          functionalImpact: rule.functionalImpact,
          references,
          notes: rule.notes,
          proxyUsed: resolution.isPrimary ? undefined : resolution.foundRsid,
          isPrimary: resolution.isPrimary,
          effect,
          context: rule.context,
          zygDesc: rule.zygDesc,
          chromosome: foundSnpData.chromosome,
          position: foundSnpData.position,
          position_bp: rule.position_bp,
        });
      } else {
        variants.push({
          gene: geneName,
          variantName: rule.label,
          rsid: primaryRsid,
          genotype: '--',
          zygosity: 'missing',
          riskAlleles: rule.riskAlleles,
          testable: rule.testable !== false,
          evidenceLevel: rule.evidence,
          functionalImpact: rule.functionalImpact,
          references: ruleReferences(rule),
          notes: rule.notes,
          isPrimary: true,
          context: rule.context,
          zygDesc: rule.zygDesc,
        });
      }
    }

    geneGroups.push({
      gene: geneName,
      variants,
      coverage: {
        found: geneSnpsFound,
        total: geneSnpsTotal,
        percentage: Math.round((geneSnpsFound / geneSnpsTotal) * 100),
      },
      references: dedupedGeneReferences(geneRules),
    });
  }

  const tabCoverage = tabSNPsTotal > 0 ? Math.round((tabSNPsFound / tabSNPsTotal) * 100) : 0;

  return {
    tabId,
    tabName,
    geneGroups,
    coverage: { found: tabSNPsFound, total: tabSNPsTotal, percentage: tabCoverage },
  };
};

const ruleReferences = (rule: NutritionRule): Reference[] =>
  rule.refs ?? rule.pubmed?.map((url) => ({ url, title: 'PubMed Reference' })) ?? [];

const dedupedGeneReferences = (geneRules: { [rsid: string]: NutritionRule }): Reference[] => {
  const refs = new Map<string, Reference>();
  for (const rule of Object.values(geneRules)) {
    rule.refs?.forEach((ref) => refs.set(ref.url, ref));
  }
  return Array.from(refs.values());
};
